/*
	machina.promise - A lightweight Promises/A compliant implementation for projects already using the machina state machine library.
	version:	0.1.1
	author:		Doug Neiner <dneiner@appendto.com>
	copyright:	2012 appendTo, LLC
	license:	Dual licensed
				MIT (http://opensource.org/licenses/mit-license)
				GPL (http://opensource.org/licenses/GPL-2.0)
*/

'use strict';

var _ = require('./underscore');
var machina = require('./machina');

var PENDING = 'pending';
var FULFILLED = 'fulfilled';
var REJECTED = 'rejected';
var PENDING_STATE = PENDING;
var FULFILLED_STATE = FULFILLED;
var REJECTED_STATE = REJECTED;
var FULFILLED_EVENT = FULFILLED;
var REJECTED_EVENT = REJECTED;

function isThenable( object ) {
	return _.isObject( object ) && 'then' in object;
}

function resolve( promise, x ) {

	if ( x === promise ) {
		promise.reject( new TypeError( '2.3.1: Throw TypeError in case of inception' ) );
		return;
	}

	if ( Promise.useExtensions ) {

		var called = false;

		var resolvePromise = function ( y ) {
			if (!called) {
				called = true;
				resolve( promise, y );
			}
		}

		var rejectPromise = function ( r ) {
			if (!called) {
				called = true;
				promise.reject( r );
			}
		}

		if ( x instanceof Promise ) {
			x.then.call( undefined, resolvePromise, promise.reject );
			return;
		}

		if ( isThenable( x ) ) {
			var then;

			try {
				then = x.then;
			} catch ( e ) {
				promise.reject( e );
				return;
			}

			if ( _.isFunction( then ) ) {
				try {
					then.call( x, resolvePromise, rejectPromise );
				} catch ( e ) {
					if (!called) {
						promise.reject( e );
					}
				}

				return;
			}
		}

	}

	promise.fulfill( x );
}

var Promise = machina.Fsm.extend({
	initialState: PENDING_STATE,
	initialize: function () {
		_.bindAll( this, 'reject', 'fulfill', 'then', 'catch', 'finally' );
	},
	reject: function ( reason ) {
		this.handle( 'process', { action: REJECTED_STATE, data: reason } );
	},
	fulfill: function ( value ) {
		this.handle( 'process', { action: FULFILLED_STATE, data: value } );
	},
	handleThen: function () {
		var fn = _.bind( this.handle, this, 'then' );

		if ( Promise.useExtensions ) {
			_.defer( fn );
		}
		else {
			fn();
		}
	},
	then: function ( onFulfilled, onRejected ) {
		var promise = new Promise();

		var callback = _.bind( function () {
			this.off( FULFILLED_EVENT, callback );
			this.off( REJECTED_EVENT, callback );

			var fulfilled = this.state === FULFILLED_STATE;
			var handler = fulfilled ? onFulfilled : onRejected;

			if ( _.isFunction( handler ) ) {
				var value;

				try {
					value = handler.apply( undefined, arguments );
				} catch ( reason ) {
					promise.reject( reason );
					return;
				}

				resolve( promise, value );
			}
			else {
				promise[ fulfilled ? 'fulfill' : 'reject' ].apply( undefined, arguments );
			}
		}, this );

		this.on( FULFILLED_EVENT, callback );
		this.on( REJECTED_EVENT, callback );

		this.handleThen();

		return promise;
	},
	'catch': function ( onRejected ) {
		return this.then( undefined, onRejected );
	},
	'finally': function ( onResolved ) {
		return this.then( function ( value ) {
			return Promise.resolve( onResolved() ).then( function () {
				return value;
			} );
		}, function ( err ) {
			return Promise.resolve( onResolved() ).then( function () {
				throw err;
			} );
		} );
	},
	states: {
		pending: {
			process: function ( params ) {
				this._data = params.data;
				this.transition( params.action );
			}
		},
		fulfilled: {
			_onEnter: function () {
				this.handleThen();
			},
			then: function () {
				this.emit( FULFILLED_EVENT, this._data );
			}
		},
		rejected: {
			_onEnter: function () {
				this.handleThen();
			},
			then: function () {
				this.emit( REJECTED_EVENT, this._data );
			}
		}
	}
} );

Promise.useExtensions = true;

Promise.resolve = function ( value ) {
	var promise = new Promise();
	promise.fulfill( value );
	return promise;
};

Promise.reject = function ( reason ) {
	var promise = new Promise();
	promise.reject( reason );
	return promise;
};

Promise.all = function ( promise /*, promises... */ ) {

	if ( !promise ) {
		return Promise.reject( new TypeError( 'Promise.all requires at least one parameter.' ) );
	}

	var promises = arguments.length === 1 && _.isArray( promise ) ? promise : _.toArray( arguments );
	var quantity = promises.length;

	var response = new Promise();
	var pending = quantity;
	var results = new Array( quantity );

	function resolve( i ) {
		return function ( value ) {
			results[i] = arguments.length > 1 ? _.toArray( arguments ) : value;
			if ( !( --pending ) ) {
				response.fulfill( results );
			}
		};
	}

	function checkResult( result, i ) {
		var each;
		if ( i in promises ) {
			each = promises[i];
			if ( isThenable( each ) ) {
				try {
					each.then(
						resolve( i ),
						response.reject
					);
				} catch ( e ) {
					response.reject( e );
				}
			}
			else {
				results[i] = each;
				--pending;
			}
		}
		else {
			--pending;
		}
	}

	_.each( results, checkResult );

	if ( !pending ) {
		response.fulfill( results );
	}

	return response;
};

Promise.race = function ( promise /*, promises... */ ) {

	if ( !promise ) {
		return Promise.reject( new TypeError( 'Promise.race requires at least one parameter.' ) );
	}

	var promises = arguments.length === 1 && _.isArray( promise ) ? promise : _.toArray( arguments );
	var response = new Promise();

	// "Cheating", because the Promise can only be resolved once.
	_.each( promises, function ( item ) {
		Promise.resolve( item ).then( response.fulfill, response.reject );
	} );

	return response;
};

module.exports = Promise;
